import { ApolloClient, InMemoryCache, ApolloLink, NetworkStatus } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { makeLogout } from 'src/utils/auth';
import { getAccessToken } from 'src/utils/tokenService';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { captureException } from '@sentry/react';
import { showErrorToast } from 'src/components/Toast';
import { gqlLog } from 'src/utils/logger';

import { API_DEBUG, isDev } from 'src/config';

const uploadLink = createUploadLink({ uri: `${process.env.BASE_URL}api/public/api/graphql` });

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      const category = extensions?.category ? String(extensions.category) : undefined;
      // Validation / authorization / authentication errors are expected, user-facing
      // business outcomes (shown as toasts) — not bugs. Only genuine/internal server
      // errors reach Sentry.
      const isExpected =
        (category && ['validation', 'authorization', 'authentication'].includes(category)) ||
        Boolean((extensions as { validation?: unknown } | undefined)?.validation);
      const isHandledAuth = message === 'Unauthenticated.';

      if (!isExpected && !isHandledAuth) {
        // Static title + explicit fingerprint keeps grouping stable and keeps the
        // dynamic server `message` (which can contain work data) out of the most
        // visible field. Only structural, non-free-text context is attached.
        captureException(new Error('GraphQL operation error'), {
          tags: {
            context: 'graphql_error',
            operation: operation.operationName ?? 'unknown',
            category: category ?? 'unknown',
          },
          fingerprint: ['graphql_error', operation.operationName ?? 'unknown', category ?? 'unknown'],
          extra: {
            path: JSON.stringify(path),
            locations: JSON.stringify(locations),
            extensionsCode: (extensions as { code?: unknown } | undefined)?.code,
          },
        });
      }

      if (isHandledAuth) {
        makeLogout({ noEvents: true });
      }
      showErrorToast(message);
    });
  }
  if (networkError) {
    showErrorToast('Network error. Please check your connection.');
    // Do NOT serialize the whole networkError — ServerError/ServerParseError embed the
    // raw response body (.result/.bodyText), which can echo submitted fields and, in
    // debug mode, SQL/stack traces. Attach an allowlist of structural fields only.
    captureException(networkError, {
      tags: {
        context: 'network_error',
        operation: operation.operationName ?? 'unknown',
      },
      extra: {
        name: networkError.name,
        statusCode: (networkError as { statusCode?: number }).statusCode,
      },
    });
  }
});

const authLink = setContext(async (operation, { headers }) => {
  const token = await getAccessToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : undefined,
    },
  };
});

// --- DEBUG: Log all GQL operations ---
let gqlRequestCount = 0;
const loggingLink = new ApolloLink((operation, forward) => {
  gqlRequestCount++;
  const num = gqlRequestCount;
  const operationType =
    operation.query.definitions[0]?.kind === 'OperationDefinition'
      ? (operation.query.definitions[0] as any).operation
      : 'unknown';
  gqlLog.debug(`#${num} ${operationType.toUpperCase()} ${operation.operationName}`, operation.variables);
  const startTime = performance.now();
  return forward(operation).map((result) => {
    const duration = Math.round(performance.now() - startTime);
    gqlLog.debug(`#${num} ${operation.operationName} completed in ${duration}ms`);
    return result;
  });
});
// --- END DEBUG ---

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([...(isDev && API_DEBUG ? [loggingLink] : []), authLink, errorLink, uploadLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          scenario(existing, { args, toReference }) {
            return existing || toReference({ __typename: 'Scenario', id: args?.id });
          },
          control(existing, { args, toReference }) {
            return existing || toReference({ __typename: 'Control', id: args?.id });
          },
          incident(existing, { args, toReference }) {
            return existing || toReference({ __typename: 'Incident', id: args?.id });
          },
        },
      },
      OrgUnitIdentity: { merge: true },
      OrgUnitActions: { merge: true },
      WorkspaceUserActions: { merge: true },
      OrganisationUserActions: { merge: true },
      ScenarioRisk: { merge: true },
      AssessmentRisk: { merge: true },
      Meta: { merge: true },
      Owner: { merge: true },
    },
  }),

  defaultOptions: {
    watchQuery: {
      errorPolicy: 'none',
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
    query: {
      errorPolicy: 'none',
      fetchPolicy: 'cache-first',
    },
  },
});

type IsQueryLoading = (options: { loading: boolean; data: any; networkStatus: NetworkStatus }) => boolean;

export const isQueryLoading: IsQueryLoading = ({ loading, data, networkStatus }) => {
  return (loading && !data) || networkStatus === NetworkStatus.refetch;
};
